import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Printer, Download } from 'lucide-react'
import { orderService, adminService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import Invoice from '../components/Invoice'

const AdminInvoicePage = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  // Fetch order details
  const { data: orderResponse, isLoading, error } = useQuery({
    queryKey: ['admin-order', id],
    queryFn: () => adminService.getOrderDetails(id),
    enabled: !!id
  })

  // Auto-print when page loads (optional - can be removed if not desired)
  useEffect(() => {
    if (orderResponse?.success && orderResponse.order) {
      // Small delay to ensure content is rendered
      const timer = setTimeout(() => {
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.get('autoprint') === 'true') {
          handlePrint()
        }
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [orderResponse])

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPDF = () => {
    // This would require a PDF generation library like jsPDF or html2pdf
    // For now, we'll use the browser's print to PDF functionality
    if (window.navigator.userAgent.includes('Chrome')) {
      window.print()
    } else {
      // For other browsers, open print dialog
      window.print()
    }
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !orderResponse?.success) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h2>
            <p className="text-gray-600 mb-6">
              The invoice you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    )
  }

  const order = orderResponse.order

  return (
    <div className="min-h-screen bg-white">
      {/* Print Controls - Hidden during print */}
      <div className="print:hidden bg-gray-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/admin/orders/${id}`)}
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Order
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Invoice #{order.order_number}
                </h1>
                <p className="text-sm text-gray-600">
                  Order placed on {new Date(order.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-700"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Invoice
              </button>
              
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:p-0 print:max-w-none">
        <Invoice order={order} />
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body {
            background: white !important;
            color: black !important;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:p-0 {
            padding: 0 !important;
          }
          
          .print\\:max-w-none {
            max-width: none !important;
          }
          
          /* Ensure proper page breaks */
          .invoice-container {
            page-break-inside: avoid;
          }
          
          /* Hide any unwanted elements during print */
          nav, header, footer, .no-print {
            display: none !important;
          }
          
          /* Ensure black text for printing */
          * {
            color: black !important;
            background: transparent !important;
          }
          
          /* Table styles for better printing */
          table {
            border-collapse: collapse !important;
          }
          
          th, td {
            border: 1px solid #000 !important;
            padding: 8px !important;
          }
          
          /* Ensure proper sizing */
          .invoice-container {
            width: 100% !important;
            max-width: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  )
}

export default AdminInvoicePage
